export type Turn = 'clockwise' | 'counter-clockwise';
export type RockColor = 'yellow' | 'green' | 'blue' | 'red' | 'white';
/**
 * Optional tag describing a notable shot. 'double' and 'multi-rock' are rendered as
 * buttons by ShotTracker but were absent from this union, so those two buttons could
 * never register a selection.
 */
export type SpecialShot =
  | 'split'
  | 'tap'
  | 'freeze'
  | 'raise'
  | 'double'
  | '2+ rocks'
  | 'multi-rock'
  | 'roll-out'
  | 'roll'
  | 'stick'
  | 'peel';
export type ShotIssue = 'crash' | 'bounced' | 'flash' | 'rule violation' | 'none';

/** Top-level grouping the shot caller selects before picking a ShotType. */
export type ShotCategory = 'guard' | 'draw' | 'hit';

/**
 * The specific shot called. Each value maps to a position zone 0-16, in the order
 * listed here: 'hogged' is zone 0, 'high-guard' zone 1, through 'peel' at zone 16.
 * See POSITION_ZONES below and ShotType.java on the server.
 */
export type ShotType =
  | 'hogged'
  | 'high-guard'
  | 'mid-guard'
  | 'tight-guard'
  | 'top-twelve'
  | 'top-eight'
  | 'top-four'
  | 'tee-line'
  | 'back-four'
  | 'back-eight'
  | 'back-twelve'
  | 'hack'
  | 'board'
  | 'control'
  | 'normal'
  | 'firm'
  | 'peel';

/** excellent=4pts, good=3pts, fair=2pts, poor=1pt, miss=0pts */
export type ShotResult = 'excellent' | 'good' | 'fair' | 'poor' | 'miss';

/**
 * Assessment of the delivered weight. Describes the outcome, not the call:
 * 'good' means the thrower hit the called weight, 'light' and 'heavy' are fail states.
 */
export type Weight = 'light' | 'good' | 'heavy';

/**
 * Why the weight missed. 'sweeping' | 'ice/rock' | 'thrower' are operator-selected,
 * 'early' | 'late' | 'on-time' are derived from the timer, and 'n/a' is the reset
 * value meaning no attribution has been made.
 */
export type WeightReason =
  | 'n/a'
  | 'sweeping'
  | 'ice/rock'
  | 'thrower'
  | 'early'
  | 'late'
  | 'on-time';

/** 'good' is the success state; 'under' and 'over' are fails. */
export type CurlAssessment = 'under' | 'good' | 'over';

/** 'n/a' is labelled "Team" in the UI: a broom/line call rather than ice or delivery. */
export type CurlReason = 'n/a' | 'ice/rock' | 'thrower';

/** Ordered list of position zones. The array index is the zone number. */
export const POSITION_ZONES: { zone: number; label: string; shotType: ShotType }[] = [
  { zone: 0, label: 'Hogged', shotType: 'hogged' },
  { zone: 1, label: 'High Guard', shotType: 'high-guard' },
  { zone: 2, label: 'Mid Guard', shotType: 'mid-guard' },
  { zone: 3, label: 'Tight Guard', shotType: 'tight-guard' },
  { zone: 4, label: 'Top Twelve', shotType: 'top-twelve' },
  { zone: 5, label: 'Top Eight', shotType: 'top-eight' },
  { zone: 6, label: 'Top Four', shotType: 'top-four' },
  { zone: 7, label: 'Tee Line', shotType: 'tee-line' },
  { zone: 8, label: 'Back Four', shotType: 'back-four' },
  { zone: 9, label: 'Back Eight', shotType: 'back-eight' },
  { zone: 10, label: 'Back Twelve', shotType: 'back-twelve' },
  { zone: 11, label: 'Hack', shotType: 'hack' },
  { zone: 12, label: 'Board', shotType: 'board' },
  { zone: 13, label: 'Control', shotType: 'control' },
  { zone: 14, label: 'Normal', shotType: 'normal' },
  { zone: 15, label: 'Firm', shotType: 'firm' },
  { zone: 16, label: 'Peel', shotType: 'peel' },
];

/** Every ShotType, in zone order. Use this to seed per-type tallies. */
export const ALL_SHOT_TYPES: ShotType[] = POSITION_ZONES.map(z => z.shotType);

const ZONE_BY_SHOT_TYPE = Object.fromEntries(
  POSITION_ZONES.map(z => [z.shotType, z.zone])
) as Record<ShotType, number>;

/** Position zone for a shot type, defaulting to the tee line for anything unmapped. */
export const getZoneFromShotType = (shotType: ShotType): number =>
  ZONE_BY_SHOT_TYPE[shotType] ?? 7;

/** Shot type occupying a zone, defaulting to the tee line for anything out of range. */
export const getShotTypeFromZone = (zone: number): ShotType =>
  POSITION_ZONES[zone]?.shotType ?? 'tee-line';

export const ALL_SHOT_RESULTS: ShotResult[] = ['excellent', 'good', 'fair', 'poor', 'miss'];

export const ALL_WEIGHTS: Weight[] = ['light', 'good', 'heavy'];

export interface Shot {
  id: string;
  timestamp: number;
  endNumber: number;
  playerName: string;
  team: 'team1' | 'team2';
  shotType: ShotType;
  broomPosition: number; // Position along tee line in feet from center (-6 to +6)
  turn: Turn; // In-turn or out-turn
  result: ShotResult;
  expectedTime: number;
  expectedCurl: number;
  weight: Weight;
  weightReason?: WeightReason;
  curlAssessment?: CurlAssessment;
  curlReason?: CurlReason;
  curl: number;
  time: number;
  actualPosition: number; // Zone where the rock stopped (0-16: 0=Hogged, 7=Tee Line, 16=Peel)
  skipped?: boolean; // Flag to indicate if shot was skipped
  isGoldStar?: boolean; // Flag for gold star shot
  isBomb?: boolean; // Flag for bomb shot
  hasVisualizerPlan?: boolean; // Whether the shot was planned via the Shot Visualizer's Set button
  notes?: string; // Optional notes about the shot
  specialShot?: SpecialShot; // Optional special shot type
  issue?: ShotIssue; // Optional issue with the shot
}

export interface End {
  number: number;
  team1Score: number;
  team2Score: number;
  shots: Shot[];
  completed?: boolean; // Flag to indicate if the end is complete
  hammerOverride?: 'team1' | 'team2'; // Manual override for which team has hammer
}

export interface ExpectedTimes {
  teeLine: number;
  hack: number;
  board: number;
  control: number;
  normal: number;
  firm: number;
  peel: number;
}

export interface Game {
  id: string;
  curlingClub?: string;
  sheetNumber?: string;
  team1Name: string;
  team2Name: string;
  team1Players: string[];
  team2Players: string[];
  // Which SavedTeam (own program's, or another's via cross-program search) team1/2
  // were copied from at setup, if any — an identity snapshot, not a live reference.
  // Null/absent when the name was typed by hand rather than selected.
  team1SourceAccountId?: string | null;
  team1SourceTeamId?: string | null;
  team2SourceAccountId?: string | null;
  team2SourceTeamId?: string | null;
  team1RockColor?: RockColor;
  team2RockColor?: RockColor;
  startsWithHammer?: 'team1' | 'team2'; // Which team starts the game with hammer
  date: number;
  ends: End[];
  isComplete: boolean;
  team1TotalScore: number;
  team2TotalScore: number;
  expectedTimes?: ExpectedTimes;
  /** Default expected curl (feet) entered at game setup, alongside expectedTimes. */
  expectedCurl?: number;
  rockMetadata?: Record<number, RockMetadata>; // Metadata for rocks 1-16
  team1RockOrder?: number[]; // Custom rock throwing order for team 1 (default: [1, 2, 3, 4, 5, 6, 7, 8])
  team2RockOrder?: number[]; // Custom rock throwing order for team 2 (default: [1, 2, 3, 4, 5, 6, 7, 8])
  team1PlayerAssignments?: number[]; // Custom player assignments for team 1 (array of player indices for each of 8 positions)
  team2PlayerAssignments?: number[]; // Custom player assignments for team 2 (array of player indices for each of 8 positions)
}

export interface RockMetadata {
  slow?: boolean;
  fast?: boolean;
  straight?: boolean;
  cutter?: boolean;
}

/** A warm-up delivery thrown before a game. */
export interface PracticeShot {
  id: string;
  timestamp: number;
  curl: number;
  time: number;
  lastStoneDraw: number; // Distance to the button in centimeters
  zone: string; // Zone label as entered, e.g. "7. Tee Line"
}

/** Pre-game warm-up data for one game. */
export interface PracticeSession {
  gameId?: string;
  shots: PracticeShot[];
  lsdValuesTeam1: number[];
  lsdValuesTeam2: number[];
  lsdHammerTeam?: 'team1' | 'team2';
}

export interface PlayerStats {
  playerName: string;
  totalShots: number;
  shotTypes: Record<ShotType, number>;
  results: Record<ShotResult, number>;
  successRate: number;
  averageWeight: Record<Weight, number>;
  averageCurl: number;
  averageTime: number;
  weightByResult: Record<Weight, { excellent: number; good: number; fair: number; poor: number; miss: number }>;
}

export interface TeamStats {
  teamName: string;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  totalPoints: number;
  totalPointsAgainst: number;
  totalShots: number;
  averagePointsPerGame: number;
  /** Traditional 0-4 shot-call scale, weighted across every shot the team has thrown. */
  traditionalScore: number;
  /** BRC weight/curl execution score — the mean of every player-game's overallScore. */
  teamExecutionScore: number | null;
  goldStars: number;
  bombs: number;
  heavyPercent: number;
  lightPercent: number;
  /**
   * Baseball-style per-end rates — see utils/teamPointsStats.ts. Both the
   * client-computed path (calculateTeamStats) and the sponsor view's
   * server-aggregated path (AnalyticsService.condensedStatsFor) fill these
   * in; kept optional only for older cached data that predates them.
   */
  totalStolenPointsFor?: number;
  totalStolenPointsGiven?: number;
  /** Actual ends played summed across every game counted — the denominator behind the four averages below. */
  endsPlayed?: number;
  /** Points For Average: points scored per end. */
  pfa?: number | null;
  /** Points Given Average: points conceded per end. */
  pga?: number | null;
  /** Stolen Points For Average: points scored without hammer, per end. */
  spfa?: number | null;
  /** Stolen Points Given Average: points conceded without hammer, per end. */
  spga?: number | null;
  /** % of the team's own hammer ends converted into 2+ points. */
  hammerConversionRate?: number | null;
  /** % of the opponent's hammer ends where this team held them to exactly 1 point. */
  forcePercent?: number | null;
  /** % of the opponent's hammer ends where this team scored anyway. */
  stealRate?: number | null;
  /** % of ends played that finished 0-0. */
  blankRate?: number | null;
  /** % of ends played where this team scored 3+. */
  bigEndRateFor?: number | null;
  /** % of ends played where the opponent scored 3+ against this team. */
  bigEndRateAgainst?: number | null;
}
